// Database Manager for PostgreSQL connection and operations
export class DatabaseManager {
  static initialize() {
    this.connection = null;
    this.isConnected = false;
    
    // Check if we're in a browser environment
    const isBrowser = typeof window !== 'undefined';
    const isProduction = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production';
    const isDevelopment = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development';
    
    this.connectionConfig = {
      host: (typeof process !== 'undefined' && process.env && process.env.DB_HOST) || 'production-db-host',
      port: (typeof process !== 'undefined' && process.env && process.env.DB_PORT) || 5432,
      database: (typeof process !== 'undefined' && process.env && process.env.DB_NAME) || 'smsf_calculator',
      user: (typeof process !== 'undefined' && process.env && process.env.DB_USER) || 'smsf_calculator_user',
      password: (typeof process !== 'undefined' && process.env && process.env.DB_PASSWORD) || 'production-password',
      ssl: isProduction ? { rejectUnauthorized: false } : false,
      max: 20,
      idleTimeoutMillis: 30000,
      connectionTimeoutMillis: 2000,
    };
    
    // DatabaseManager initialized
  }

  // Connect to database
  static async connect() {
    try {
      if (this.isConnected) {
        return this.connection;
      }

      // In a real implementation, you would use pg or similar
      // For now, we'll simulate the connection
      this.connection = {
        query: this.mockQuery.bind(this),
        end: () => { this.isConnected = false; }
      };
      
      this.isConnected = true;
      // Database connected successfully
      
      return this.connection;
    } catch (error) {
      // Database connection error - handled silently
      throw error;
    }
  }

  // Mock query function for development
  static async mockQuery(sql, params = []) {
    // Simulate database delay
    await new Promise(resolve => setTimeout(resolve, 10));
    
    // Mock DB Query - handled silently
    
    // Return mock data based on query type
    if (sql.includes('SELECT') && sql.includes('users')) {
      return {
        rows: [
          {
            id: 'mock-user-id',
            email: 'test@example.com',
            first_name: 'Test',
            last_name: 'User',
            created_at: new Date(),
            subscription_status: 'free'
          }
        ],
        rowCount: 1
      };
    }
    
    if (sql.includes('INSERT')) {
      return {
        rows: [{ id: 'mock-id-' + Date.now() }],
        rowCount: 1
      };
    }
    
    return { rows: [], rowCount: 0 };
  }

  // Execute a query
  static async query(sql, params = []) {
    try {
      const connection = await this.connect();
      const result = await connection.query(sql, params);
      
      // Log query in development
      const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development';
      if (isDev) {
        // DB Query - handled silently
      }
      
      return result;
    } catch (error) {
      // Database query error - handled silently
      throw error;
    }
  }

  // Get user by email
  static async getUserByEmail(email) {
    const sql = 'SELECT * FROM users WHERE email = $1';
    const result = await this.query(sql, [email]);
    return result.rows[0] || null;
  }

  // Get user by ID
  static async getUserById(id) {
    const sql = 'SELECT * FROM users WHERE id = $1';
    const result = await this.query(sql, [id]);
    return result.rows[0] || null;
  }

  // Create user
  static async createUser(userData) {
    const { email, passwordHash, firstName, lastName } = userData;
    const sql = `
      INSERT INTO users (email, password_hash, first_name, last_name, is_verified)
      VALUES ($1, $2, $3, $4, $5)
      RETURNING *
    `;
    const result = await this.query(sql, [email, passwordHash, firstName, lastName, false]);
    return result.rows[0];
  }

  // Update user
  static async updateUser(id, updateData) {
    const fields = [];
    const values = [];
    let paramCount = 1;

    for (const [key, value] of Object.entries(updateData)) {
      if (value !== undefined) {
        fields.push(`${key} = $${paramCount}`);
        values.push(value);
        paramCount++;
      }
    }

    if (fields.length === 0) {
      throw new Error('No fields to update');
    }

    values.push(id);
    const sql = `UPDATE users SET ${fields.join(', ')} WHERE id = $${paramCount} RETURNING *`;
    const result = await this.query(sql, values);
    return result.rows[0];
  }

  // Save calculator scenario
  static async saveScenario(userId, scenarioData) {
    const { name, calculatorType, data } = scenarioData;
    const sql = `
      INSERT INTO calculator_scenarios (user_id, name, calculator_type, scenario_data)
      VALUES ($1, $2, $3, $4)
      RETURNING *
    `;
    const result = await this.query(sql, [userId, name, calculatorType, JSON.stringify(data)]);
    return result.rows[0];
  }

  // Get user scenarios
  static async getUserScenarios(userId, calculatorType = null) {
    let sql = 'SELECT * FROM calculator_scenarios WHERE user_id = $1';
    const params = [userId];

    if (calculatorType) {
      sql += ' AND calculator_type = $2';
      params.push(calculatorType);
    }

    sql += ' ORDER BY updated_at DESC';
    const result = await this.query(sql, params);
    return result.rows;
  }

  // Get public scenarios
  static async getPublicScenarios(calculatorType = null, limit = 10) {
    let sql = 'SELECT * FROM calculator_scenarios WHERE is_public = true';
    const params = [];

    if (calculatorType) {
      sql += ' AND calculator_type = $1';
      params.push(calculatorType);
    }

    sql += ' ORDER BY created_at DESC LIMIT $' + (params.length + 1);
    params.push(limit);

    const result = await this.query(sql, params);
    return result.rows;
  }

  // Create user session
  static async createSession(userId, sessionToken, expiresAt, ipAddress, userAgent) {
    const sql = `
      INSERT INTO user_sessions (user_id, session_token, expires_at, ip_address, user_agent)
      VALUES ($1, $2, $3, $4, $5)
      RETURNING *
    `;
    const result = await this.query(sql, [userId, sessionToken, expiresAt, ipAddress, userAgent]);
    return result.rows[0];
  }

  // Get session by token
  static async getSessionByToken(token) {
    const sql = `
      SELECT s.*, u.email, u.first_name, u.last_name, u.subscription_status
      FROM user_sessions s
      JOIN users u ON s.user_id = u.id
      WHERE s.session_token = $1 AND s.expires_at > NOW() AND s.is_active = true
    `;
    const result = await this.query(sql, [token]);
    return result.rows[0] || null;
  }

  // Log audit event
  static async logAuditEvent(userId, action, resourceType = null, resourceId = null, details = null, ipAddress = null, userAgent = null) {
    const sql = `
      INSERT INTO audit_logs (user_id, action, resource_type, resource_id, details, ip_address, user_agent)
      VALUES ($1, $2, $3, $4, $5, $6, $7)
      RETURNING *
    `;
    const result = await this.query(sql, [
      userId,
      action,
      resourceType,
      resourceId,
      details ? JSON.stringify(details) : null,
      ipAddress,
      userAgent
    ]);
    return result.rows[0];
  }

  // Log compliance violation
  static async logComplianceViolation(userId, scenarioId, violationType, severity, message, details = null) {
    const sql = `
      INSERT INTO compliance_violations (user_id, scenario_id, violation_type, severity, message, details)
      VALUES ($1, $2, $3, $4, $5, $6)
      RETURNING *
    `;
    const result = await this.query(sql, [
      userId,
      scenarioId,
      violationType,
      severity,
      message,
      details ? JSON.stringify(details) : null
    ]);
    return result.rows[0];
  }

  // Log analytics event
  static async logAnalyticsEvent(userId, sessionId, eventName, eventProperties = null, ipAddress = null, userAgent = null) {
    const sql = `
      INSERT INTO analytics_events (user_id, session_id, event_name, event_properties, ip_address, user_agent)
      VALUES ($1, $2, $3, $4, $5, $6)
      RETURNING *
    `;
    const result = await this.query(sql, [
      userId,
      sessionId,
      eventName,
      eventProperties ? JSON.stringify(eventProperties) : null,
      ipAddress,
      userAgent
    ]);
    return result.rows[0];
  }

  // Get system setting
  static async getSystemSetting(key) {
    const sql = 'SELECT value FROM system_settings WHERE key = $1';
    const result = await this.query(sql, [key]);
    return result.rows[0]?.value || null;
  }

  // Update system setting
  static async updateSystemSetting(key, value) {
    const sql = `
      INSERT INTO system_settings (key, value)
      VALUES ($1, $2)
      ON CONFLICT (key) DO UPDATE SET value = $2, updated_at = CURRENT_TIMESTAMP
      RETURNING *
    `;
    const result = await this.query(sql, [key, JSON.stringify(value)]);
    return result.rows[0];
  }

  // Get subscription plans
  static async getSubscriptionPlans() {
    const sql = 'SELECT * FROM subscription_plans WHERE is_active = true ORDER BY price_monthly ASC';
    const result = await this.query(sql);
    return result.rows;
  }

  // Disconnect from database
  static async disconnect() {
    try {
      if (this.connection && this.isConnected) {
        await this.connection.end();
        this.isConnected = false;
        // Database disconnected
      }
    } catch (error) {
      // Database disconnection error - handled silently
    }
  }

  // Health check
  static async healthCheck() {
    try {
      const result = await this.query('SELECT 1 as health');
      return {
        status: 'healthy',
        connected: this.isConnected,
        response: result.rows[0]
      };
    } catch (error) {
      return {
        status: 'unhealthy',
        connected: false,
        error: error.message
      };
    }
  }
}